import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, ImageIcon, PlusCircle, Trash2, X } from 'lucide-react';
import { MediaPicker } from '../../../components/cms/MediaPicker';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RichTextEditor } from '../../../components/ui/rich-text-editor';
import { useStandort } from '../../../components/layouts/use-standort';
import { usePermissions } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import { jobangeboteApi, type Jobangebot } from '../../../lib/api/jobangebote';

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const leer = (): Jobangebot => ({ title: '', type: '', hours: '', desc: '', image_url: '' });

/**
 * Offene Stellen eines Standorts (Entscheidung F02, 2026-09-21): gepflegt mit den
 * Recruiting-Rechten, nicht mit den Website-Rechten. Gespeichert wird ausschließlich
 * die Stellenliste des Standorts; die übrigen Website-Inhalte bleiben unberührt.
 */
const StandortJobsPage: React.FC = () => {
  const { selectedStandortId, selectedStandort } = useStandort();
  const { can } = usePermissions();
  const [jobs, setJobs] = useState<Jobangebot[]>([]);
  // T-0214: Bild aus der Medienbibliothek statt nur als Adresse.
  const [bildFuer, setBildFuer] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const darfBearbeiten =
    selectedStandortId != null &&
    can(P.recruiting.jobangebote.jobangebote_bearbeiten, { standortId: selectedStandortId });
  const darfVeroeffentlichen =
    selectedStandortId != null &&
    can(P.recruiting.jobangebote.jobangebote_veroeffentlichen, { standortId: selectedStandortId });

  const load = useCallback(async () => {
    if (!selectedStandortId) return;
    setLoading(true);
    try {
      const data = await jobangeboteApi.get(selectedStandortId);
      setJobs(data.jobs.length ? data.jobs : []);
      setPublishedAt(data.published_at);
      setDirty(false);
    } catch (e) {
      toast.error(errorText(e, 'Stellen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }, [selectedStandortId]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = (index: number, patch: Partial<Jobangebot>) => {
    setJobs((prev) => prev.map((job, i) => (i === index ? { ...job, ...patch } : job)));
    setDirty(true);
  };

  const move = (index: number, richtung: -1 | 1) => {
    setJobs((prev) => {
      const ziel = index + richtung;
      if (ziel < 0 || ziel >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[ziel]] = [next[ziel], next[index]];
      return next;
    });
    setDirty(true);
  };

  const speichern = async (): Promise<boolean> => {
    if (!selectedStandortId) return false;
    if (jobs.some((job) => !job.title.trim())) {
      toast.error('Jede Stelle braucht einen Titel');
      return false;
    }
    setSaving(true);
    try {
      const data = await jobangeboteApi.save(selectedStandortId, jobs);
      setJobs(data.jobs);
      setDirty(false);
      return true;
    } catch (e) {
      toast.error(errorText(e, 'Speichern fehlgeschlagen'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const veroeffentlichen = async () => {
    if (!selectedStandortId) return;
    if (dirty && !(await speichern())) return;
    setSaving(true);
    try {
      const data = await jobangeboteApi.publish(selectedStandortId);
      setPublishedAt(data.published_at);
      toast.success('Stellen sind auf der Website veröffentlicht');
    } catch (e) {
      toast.error(errorText(e, 'Veröffentlichen fehlgeschlagen'));
    } finally {
      setSaving(false);
    }
  };

  if (!selectedStandortId) {
    return (
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Bitte oben einen Standort auswählen, um dessen Stellen zu pflegen.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Stellenangebote</CardTitle>
            <CardDescription>
              Offene Stellen für {selectedStandort?.name ?? 'diesen Standort'}. Bewerbungen gehen an
              die Standort-E-Mail.{' '}
              {publishedAt
                ? `Zuletzt veröffentlicht am ${new Date(publishedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })} Uhr.`
                : 'Noch nicht veröffentlicht.'}
            </CardDescription>
          </div>
          <div className="flex shrink-0 gap-2">
            {darfBearbeiten && (
              <Button variant="outline" onClick={() => void speichern()} disabled={saving || !dirty}>
                Entwurf speichern
              </Button>
            )}
            {darfVeroeffentlichen && (
              <Button onClick={() => void veroeffentlichen()} disabled={saving}>
                Veröffentlichen
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && <p className="text-sm text-muted-foreground">Lade Stellen…</p>}
          {!loading && jobs.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Noch keine offenen Stellen an diesem Standort.
            </p>
          )}
          {jobs.map((job, index) => (
            <div key={index} className="space-y-3 rounded-md border p-4">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 pt-6">
                  <button type="button" className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label="Nach oben" title="Nach oben" disabled={!darfBearbeiten || index === 0}
                    onClick={() => move(index, -1)}>
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button type="button" className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label="Nach unten" title="Nach unten" disabled={!darfBearbeiten || index === jobs.length - 1}
                    onClick={() => move(index, 1)}>
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid flex-1 gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-3">
                    <Label htmlFor={`job-title-${index}`}>Titel</Label>
                    <Input id={`job-title-${index}`} uiSize="md" value={job.title} disabled={!darfBearbeiten}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => update(index, { title: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor={`job-type-${index}`}>Art</Label>
                    <Input id={`job-type-${index}`} uiSize="md" value={job.type ?? ''} placeholder="z. B. Freiberuflich"
                      disabled={!darfBearbeiten}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => update(index, { type: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor={`job-hours-${index}`}>Umfang</Label>
                    <Input id={`job-hours-${index}`} uiSize="md" value={job.hours ?? ''} placeholder="z. B. Teilzeit"
                      disabled={!darfBearbeiten}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => update(index, { hours: e.target.value })} />
                  </div>
                  <div>
                    <Label>Bild (optional)</Label>
                    <div className="mt-1 flex items-center gap-2">
                      {job.image_url ? (
                        <img src={job.image_url} alt="" className="h-10 w-14 rounded border object-cover" />
                      ) : null}
                      <Button type="button" variant="outline" size="sm" disabled={!darfBearbeiten}
                        onClick={() => setBildFuer(index)}>
                        <ImageIcon className="mr-1 h-4 w-4" /> {job.image_url ? 'Ändern' : 'Auswählen'}
                      </Button>
                      {job.image_url && darfBearbeiten ? (
                        <Button type="button" variant="ghost" size="sm" aria-label="Bild entfernen"
                          onClick={() => update(index, { image_url: '' })}>
                          <X className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
                {darfBearbeiten && (
                  <Button variant="outline" size="sm" className="mt-6" aria-label="Stelle entfernen"
                    onClick={() => { setJobs((prev) => prev.filter((_, i) => i !== index)); setDirty(true); }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div>
                <Label>Beschreibung</Label>
                <RichTextEditor value={job.desc ?? ''} onChange={(html) => update(index, { desc: html })} />
              </div>
            </div>
          ))}
          {darfBearbeiten && (
            <Button variant="outline" onClick={() => { setJobs((prev) => [...prev, leer()]); setDirty(true); }}>
              <PlusCircle className="mr-2 h-4 w-4" /> Stelle hinzufügen
            </Button>
          )}
        </CardContent>
      </Card>
      <MediaPicker
        open={bildFuer !== null}
        onClose={() => setBildFuer(null)}
        onSelect={(url) => {
          if (bildFuer !== null) update(bildFuer, { image_url: url });
          setBildFuer(null);
        }}
      />
    </div>
  );
};

export default StandortJobsPage;
